import React, { useEffect, useState, useCallback, useRef } from "react"
import { useDispatch } from "react-redux"
import { X } from "lucide-react"
import AdminCheckInForm, {
  EMPTY_CHECK_IN_FORM,
  mapCheckInToForm,
  EMPTY_END_OF_YEAR_FORM,
  mapEndOfYearCheckInToForm,
} from "../../Components/admin/AdminCheckInForm"
import { uploadCheckInEvidence } from "../../redux/adminCheckInSlice"

const getEmptyForm = (checkInType, adminName) => {
  if (checkInType === "end_of_year") {
    return {
      ...EMPTY_END_OF_YEAR_FORM,
      leaderName: adminName || "",
    }
  }

  return {
    ...EMPTY_CHECK_IN_FORM,
    leaderName: adminName || "",
  }
}

const mapCheckInForm = (checkIn, checkInType) => {
  if (checkInType === "end_of_year" || checkIn?.checkInType === "end_of_year") {
    return mapEndOfYearCheckInToForm(checkIn)
  }

  return mapCheckInToForm(checkIn)
}

const AdminCheckInModal = ({
  isOpen,
  onClose,
  mode = "initiate",
  checkInType = "mid_year",
  adminName,
  checkIn,
  onInitiate,
  onAdminSubmit,
  onConferenceSubmit,
  loading = false,
}) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState(() => getEmptyForm(checkInType, adminName))
  const [uploadingEvidence, setUploadingEvidence] = useState(false)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      if (checkIn) {
        setFormData(mapCheckInForm(checkIn, checkInType))
      } else {
        setFormData(getEmptyForm(checkInType, adminName))
      }
      initializedRef.current = true
    }
  }, [isOpen, checkIn, adminName, checkInType])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleUploadEvidence = async (files) => {
    try {
      setUploadingEvidence(true)
      const uploaded = await dispatch(uploadCheckInEvidence(files)).unwrap()
      setFormData((prev) => ({
        ...prev,
        evidenceAttachments: [...(prev.evidenceAttachments || []), ...uploaded],
      }))
    } catch {
      // toast handled in slice
    } finally {
      setUploadingEvidence(false)
    }
  }

  const handleSubmit = () => {
    if (mode === "initiate") {
      onInitiate?.()
      return
    }

    if (mode === "admin") {
      if (checkInType === "end_of_year") {
        onAdminSubmit?.({
          leaderName: formData.leaderName,
          leaderLocation: formData.leaderLocation,
          impactOnPractice: formData.impactOnPractice,
          impactOnStudentLearning: formData.impactOnStudentLearning,
          challengesAndNextSteps: formData.challengesAndNextSteps,
          evidenceAttachments: formData.evidenceAttachments || [],
          reflectionDate: formData.reflectionDate || null,
        })
        return
      }

      onAdminSubmit?.({
        leaderName: formData.leaderName,
        leaderLocation: formData.leaderLocation,
        selfReflection: formData.selfReflection,
        evidenceAttachments: formData.evidenceAttachments || [],
      })
      return
    }

    if (mode === "super_admin_conference") {
      if (checkInType === "end_of_year") {
        onConferenceSubmit?.({
          conferenceDate: formData.conferenceDate || null,
          summativeFeedback: formData.summativeFeedback,
          developmentImpact: formData.developmentImpact,
          partnerImpact: formData.partnerImpact,
          communityImpact: formData.communityImpact,
          evaluativeCycleComplete: formData.evaluativeCycleComplete,
          tier2Support: formData.tier2Support,
          tier2Strategies: formData.tier2Strategies,
          multiYearContinue: formData.multiYearContinue,
          multiYearAdjust: formData.multiYearAdjust,
          multiYearCompleted: formData.multiYearCompleted,
          multiYearNotes: formData.multiYearNotes,
        })
        return
      }

      onConferenceSubmit?.({
        conferenceDate: formData.conferenceDate || null,
        feedbackToLeader: formData.feedbackToLeader,
      })
    }
  }

  const getSubmitLabel = () => {
    if (mode === "initiate") {
      return checkInType === "mid_year"
        ? "Initiate Mid-Year Check-in"
        : "Initiate End-of-Year Reflection"
    }
    if (mode === "admin") return "Submit to Super Admin"
    if (mode === "super_admin_conference") return "Complete Conference"
    return "Close"
  }

  const getTitle = () =>
    checkInType === "mid_year" ? "Mid-Year Check-in" : "End-of-Year Reflection"

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#241763]">{getTitle()}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close check-in modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mode === "admin" && (
            <p className="text-sm text-gray-600 mb-4">
              Complete your reflection sections and upload evidence. The
              conference section will be completed by the Super Admin.
            </p>
          )}

          <AdminCheckInForm
            formData={formData}
            onChange={handleChange}
            mode={mode}
            checkInType={checkInType}
            adminName={adminName}
            onUploadEvidence={handleUploadEvidence}
            uploadingEvidence={uploadingEvidence}
          />
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Cancel
          </button>

          {mode !== "view" && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || uploadingEvidence}
              className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
            >
              {loading ? "Please wait..." : getSubmitLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminCheckInModal
